import { useState, useRef, useEffect } from "react";
import {
    SettingsIcon,
    CalendarIcon,
    InfoIcon,
    BracesIcon,
    XIcon,
    Loader2Icon,
} from "lucide-react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Dialog, DialogContent } from "@component/dialog";
import { Tooltip } from "@component/tooltip";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { SendCustomizedEmailRequest } from "@type/candidate";
import { Applicant } from "@type/applicant";
import { Job } from "@type/job";
import { EmailTemplate } from "@type/setting";
import { useUserProfileContext } from "@context/user_profile";
import EmailVariablesPanel from "@container/email-variable-panel";
import TextEditor, { TextEditorRef } from "./rich-editor/v2/text-editor";
import { getEmailTemplates } from "@service/employer";
import { defaultAssessmentInvitationTemplate, defaultAssessmentReminderTemplate, defaultAssessmentRetakeTemplate, defaultHireTemplate, defaultInterviewInvitationTemplate, defaultJobInvitationTemplate, defaultRejectTemplate } from "@constant/email";
import { mergeEmailTemplates } from "@util/email";
import { Link } from "react-router-dom";
import { getCompanyInfo } from "@service/user";
import { Company } from "@type/company";
import RetakeInterviewConfirmDialog from "@container/dialog/dialog-retake-interview-confirm";

interface Props {
    isOpen: boolean;
    onClose: (open: boolean) => void;
    applicants?: Applicant[]
    job: Job | null;
    type: "send-invitation" | "reminder" | "retake" | "resend-invitation";
    onSendInvitation: (body: SendCustomizedEmailRequest) => void | Promise<void>;
}

export default function AssessmentInvitationDialogV2({
    isOpen,
    onClose,
    applicants = [],
    job,
    type,
    onSendInvitation
}: Props) {
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
    const [listTemplates, setListTemplates] = useState<EmailTemplate[]>([]);
    const [senderName, setSenderName] = useState("ourteam");
    const [replyTo, setReplyTo] = useState<string>("work@ourteam.ai");
    const [lastFocusedField, setLastFocusedField] = useState<"subject" | "content">("content");
    const [showVariablesPanel, setShowVariablesPanel] = useState(false);
    const [companyInfo, setCompanyInfo] = useState<Company | undefined>(undefined);
    const [selectedApplicants, setSelectedApplicants] = useState<Applicant[]>([]);
    const [showRetakeWarning, setShowRetakeWarning] = useState(false);

    // Refs for form elements
    const subjectInputRef = useRef<HTMLInputElement>(null);
    const contentEditorRef = useRef<TextEditorRef>(null);

    const reloadTemplates = () => {
        setIsLoadingData(true);
        getEmailTemplates().then((resp) => {
            if (resp.status === 200) {
                const hardcodedDefaults: EmailTemplate[] = [
                    {
                        template_id: "default_reject",
                        template_name: "Standard Rejection Email",
                        template_category: "reject",
                        email_subject: defaultRejectTemplate(companyInfo).subject,
                        email_content: defaultRejectTemplate(companyInfo).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_hire",
                        template_name: "Standard Job Offer Email",
                        template_category: "hire",
                        email_subject: defaultHireTemplate(companyInfo).subject,
                        email_content: defaultHireTemplate(companyInfo).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_interview_invitation",
                        template_name: "Standard Interview Invitation Email",
                        template_category: "interview-invitation",
                        email_subject: defaultInterviewInvitationTemplate(companyInfo).subject,
                        email_content: defaultInterviewInvitationTemplate(companyInfo).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_job_invitation",
                        template_name: "Standard Job Invitation Email",
                        template_category: "job-invitation",
                        email_subject: defaultJobInvitationTemplate(companyInfo).subject,
                        email_content: defaultJobInvitationTemplate(companyInfo).content,
                        is_default: true,
                    },
                ];

                if (type === "reminder") {
                    hardcodedDefaults.push({
                        template_id: "default_assessment_reminder",
                        template_name: "Standard Screening Reminder Email",
                        template_category: "assessment-invitation",
                        email_subject: defaultAssessmentReminderTemplate().subject,
                        email_content: defaultAssessmentReminderTemplate().content,
                        is_default: true,
                    });
                } else if (type === "retake") {
                    hardcodedDefaults.push({
                        template_id: "default_assessment_retake",
                        template_name: "Standard Interview Request Retake Email",
                        template_category: "interview-invitation",
                        email_subject: defaultAssessmentRetakeTemplate().subject,
                        email_content: defaultAssessmentRetakeTemplate().content,
                        is_default: true,
                    });
                } else {
                    hardcodedDefaults.push({
                        template_id: "default_assessment_invitation",
                        template_name: "Standard Screening Invitation Email",
                        template_category: "assessment-invitation",
                        email_subject: defaultAssessmentInvitationTemplate(job?.application_expiry_days || 7).subject,
                        email_content: defaultAssessmentInvitationTemplate(job?.application_expiry_days || 7).content,
                        is_default: true,
                    });
                }

                const merged = mergeEmailTemplates(resp.data.data, hardcodedDefaults);
                setListTemplates(merged);

                if (type === "reminder") {
                    setSelectedTemplate(merged.find(t => t.template_id === "default_assessment_reminder") ?? null);
                } else if (type === "retake") {
                    setSelectedTemplate(merged.find(t => t.template_id === "default_assessment_retake") ?? null);
                } else {
                    setSelectedTemplate(merged.find(t => t.template_id === "default_assessment_invitation") ?? null);
                }
            }
        }).finally(() => setIsLoadingData(false))
    }

    useEffect(() => {
        if (isOpen) {
            setSelectedApplicants(applicants)
            reloadTemplates()
        }
        return () => {
            setSelectedTemplate(null);
            setIsLoading(false);
            setIsLoadingData(false);
        }
    }, [isOpen])

    useEffect(() => {
        if (isOpen && !isLoadingUserProfile && userInfo) {
            getCompanyInfo(userInfo.company_id).then((resp) => {
                if (resp.status === 200) {
                    setCompanyInfo(resp.data.data);
                    setSenderName(job?.outgoing_email?.sender_name ? job.outgoing_email.sender_name : resp.data.data.company_metadata?.outgoing_email?.sender_name || "ourteam");
                    setReplyTo(job?.outgoing_email?.default_reply_to ? job.outgoing_email.default_reply_to : resp.data.data.company_metadata?.outgoing_email?.default_reply_to || "work@ourteam.ai");
                }
            })
        }
    }, [isOpen, isLoadingUserProfile])


    const handleTemplateChange = (templateID: string) => {
        const template = listTemplates.find(t => t.template_id === templateID);
        if (template) {
            setSelectedTemplate(template);
        } else {
            setSelectedTemplate(null);
        }
    };

    const handleInsertVariable = (variable: string) => {
        // Get references to both fields using refs
        const subjectInput = subjectInputRef.current;
        const contentEditor = contentEditorRef.current;

        // Insert into the last focused field
        if (lastFocusedField === "subject" && subjectInput) {
            const start = subjectInput.selectionStart || 0;
            const end = subjectInput.selectionEnd || 0;
            const newSubject =
                selectedTemplate?.email_subject.substring(0, start) +
                variable +
                selectedTemplate?.email_subject.substring(end);
            setSelectedTemplate((prev) => prev ? { ...prev, email_subject: newSubject } : null);

            // Set cursor position after the inserted variable
            setTimeout(() => {
                subjectInput.focus();
                subjectInput.setSelectionRange(
                    start + variable.length,
                    start + variable.length
                );
            }, 0);
        } else if (contentEditor) {
            // Insert text at current cursor position in the rich text editor
            contentEditor.insertText(variable);

            // Focus the editor after insertion
            setTimeout(() => {
                contentEditor.focus();
            }, 0);
        } else {
            // Fallback: append to end of content
            setSelectedTemplate((prev) => prev ? { ...prev, email_content: (prev.email_content + " " + variable) } : null);
        }
    }

    const handleSendInvitation = async () => {
        if (type === "retake") {
            setShowRetakeWarning(true);
            return;
        }

        setIsLoading(true);
        try {
            await onSendInvitation({
                user_ids: applicants.filter(applicant => applicant.is_selected).map(applicant => applicant.applicant_id) || [],
                sender_name: senderName,
                reply_to: replyTo,
                email_subject: selectedTemplate?.email_subject || "",
                email_body: selectedTemplate?.email_content || "",
                attachment: null,
                job_id: job?.id || "",
                assessment_invitation: true,
                assessment_invitation_type: type,
                interview_invitation: false,
                reject_candidate: false,
                applicant_ids: applicants.filter(applicant => applicant.is_selected).map(applicant => applicant.id) || [],
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendRetakeConfirmation = async () => {
        setIsLoading(true);
        try {
            await onSendInvitation({
                user_ids: applicants.filter(applicant => applicant.is_selected).map(applicant => applicant.applicant_id) || [],
                sender_name: senderName,
                reply_to: replyTo,
                email_subject: selectedTemplate?.email_subject || "",
                email_body: selectedTemplate?.email_content || "",
                attachment: null,
                job_id: job?.id || "",
                assessment_invitation: true,
                assessment_invitation_type: "retake",
                interview_invitation: false,
                reject_candidate: false,
                applicant_ids: applicants.filter(applicant => applicant.is_selected).map(applicant => applicant.id) || [],
            });
        } finally {
            setIsLoading(false);
        }
        setShowRetakeWarning(false);
    }

    const listSelectedApplicantEmails = selectedApplicants.filter(applicant => applicant.is_selected) || [];

    return (
        <>
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="max-w-[1400px] w-[98vw] h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
                    {/* Single Unified Container */}
                    <div className="flex flex-col flex-1 min-h-0 p-6">
                        {/* Header - Fixed */}
                        <div className="mb-6 flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                                    <CalendarIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-foreground">
                                        {type === "send-invitation" ? "Send Screening Invitation" :
                                            type === "reminder" ? "Send Screening Reminder" :
                                                type === "retake" ? "Request to Retake Interview" :
                                                    "Resend Screening Invitation"}
                                    </h2>
                                    <p className="text-sm text-muted-foreground">
                                        {type === "send-invitation" ? (
                                            <>
                                                This will send an email to {listSelectedApplicantEmails.length}{" "}
                                                candidate{listSelectedApplicantEmails.length !== 1 ? "s" : ""} with a
                                                unique link to begin the screening.
                                            </>
                                        ) : type === "reminder" ? (
                                            <>
                                                This will send a reminder email to {listSelectedApplicantEmails.length}{" "}
                                                candidate{listSelectedApplicantEmails.length !== 1 ? "s" : ""} to complete their screening.
                                            </>
                                        ) : type === "retake" ? (
                                            <>
                                                This will notify {listSelectedApplicantEmails.length} candidate{listSelectedApplicantEmails.length !== 1 ? "s" : ""} to retake their AI interview. Once sent, their previous interview will reset and no longer be available.
                                            </>
                                        ) : (
                                            <>
                                                This will resend the screening invitation email to {listSelectedApplicantEmails.length}{" "}
                                                candidate{listSelectedApplicantEmails.length !== 1 ? "s" : ""}.
                                            </>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Main Content - Flexible Layout */}
                        <div className="flex flex-1 min-h-0 gap-6 overflow-hidden">
                            {/* Left Column - Form Fields */}
                            <div
                                className={`${showVariablesPanel ? "w-1/3" : "w-1/2"} space-y-5 overflow-y-auto pr-2`}
                                style={{ paddingRight: "8px", paddingLeft: "8px" }}
                            >
                                <div>
                                    <Label
                                        htmlFor="recipient-emails"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        Recipients ({listSelectedApplicantEmails.length})
                                    </Label>

                                    {/* Email tags container */}
                                    <div className="mt-2 min-h-[46px] border border-input rounded-md p-2 flex flex-wrap gap-2 items-center bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                        {/* Individual email tags */}
                                        {listSelectedApplicantEmails.map((applicant, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center gap-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-md text-sm"
                                            >
                                                <span>{applicant.main_email}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedApplicants((prev) =>
                                                            prev.map((item) =>
                                                                item.applicant_id === applicant.applicant_id
                                                                    ? { ...item, is_selected: false }
                                                                    : item
                                                            )
                                                        );
                                                    }}
                                                    className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                                                >
                                                    <XIcon className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Company Selection */}
                                <div>
                                    <Label
                                        htmlFor="company-select"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        Company <span className="text-red-500">*</span>
                                    </Label>
                                    {job && (
                                        <Input
                                            value={(job?.main_company_name === job?.company_name ? job?.main_company_name : `${job?.company_name || ""} by ${job?.main_company_name || ""}`) || ""}
                                            disabled
                                            className="mt-2 bg-gray-50 dark:bg-gray-900 cursor-not-allowed"
                                        />
                                    )}
                                </div>

                                {/* Job Position Selection */}
                                <div>
                                    <Label
                                        htmlFor="job-position"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        Job Listing <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        value={job?.job_title}
                                        disabled
                                        className="mt-2 bg-gray-50 dark:bg-gray-900 cursor-not-allowed"
                                    />

                                    {/* Job Details Banner */}
                                    {job && (
                                        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 space-y-3">
                                            <div>
                                                <Label className="text-xs font-medium text-muted-foreground mb-1">
                                                    Job Title
                                                </Label>
                                                <Input
                                                    value={job.job_title}
                                                    disabled
                                                    className="text-sm font-medium bg-white dark:bg-gray-900"
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <Label className="text-xs font-medium text-muted-foreground mb-1">
                                                        Department
                                                    </Label>
                                                    <Input
                                                        value={job.department}
                                                        disabled
                                                        className="text-sm bg-white dark:bg-gray-900"
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="text-xs font-medium text-muted-foreground mb-1">
                                                        Location
                                                    </Label>
                                                    <Input
                                                        value={job.location}
                                                        disabled
                                                        className="text-sm bg-white dark:bg-gray-900"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Template Selector */}
                                <div>
                                    <div className="flex items-center gap-2">
                                        <Label className="text-sm font-medium text-foreground">
                                            Email Template
                                        </Label>
                                        <Tooltip side="right" content={(<p>You can edit this email template but the original template won't be affected.</p>)}>
                                            <InfoIcon className="w-4 h-4 text-blue-600 cursor-help" />
                                        </Tooltip>
                                    </div>
                                    <div className="flex items-center gap-2 mt-2 mb-3">
                                        <Select
                                            value={selectedTemplate?.template_id || ""}
                                            onValueChange={handleTemplateChange}
                                        >
                                            <SelectTrigger className="flex-1">
                                                <SelectValue placeholder="Choose a template" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {listTemplates.filter((template) => (type !== "retake" && template.template_category === 'assessment-invitation') || (type === "retake" && template.template_category === "interview-invitation")).length === 0 ? (
                                                    <SelectItem value="none" disabled>
                                                        No templates available
                                                    </SelectItem>
                                                ) : (
                                                    <>
                                                        {listTemplates.filter((template) => (type !== "retake" && template.template_category === 'assessment-invitation') || (type === "retake" && template.template_category === "interview-invitation")).map((template, key) => (
                                                            <SelectItem key={key} value={template.template_id || ""}>
                                                                <div className="flex items-center justify-between w-full">
                                                                    <span>{template.template_name}</span>
                                                                    {template.is_default && (
                                                                        <span
                                                                            className="ml-2 px-2 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full"
                                                                            style={{
                                                                                paddingTop: "4px",
                                                                                paddingBottom: "4px",
                                                                            }}
                                                                        >
                                                                            Default
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </SelectItem>
                                                        ))}
                                                    </>
                                                )}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Email Content */}
                            <div
                                className={`${showVariablesPanel ? "w-1/3" : "w-1/2"} flex flex-col min-h-0 pl-2 overflow-y-auto`}
                                style={{ paddingRight: "8px" }}
                            >
                                {/* Subject */}
                                <div className="mb-4">
                                    <div className="flex items-center gap-2">
                                        <Label
                                            htmlFor="email-subject"
                                            className="text-sm font-medium text-foreground"
                                        >
                                            Email Subject
                                        </Label>
                                        <Tooltip side="right" content={(
                                            <p>Personalize your email subject using variables.</p>
                                        )}>
                                            <InfoIcon className="w-4 h-4 text-blue-600 cursor-help" />
                                        </Tooltip>
                                    </div>
                                    <Input
                                        ref={subjectInputRef}
                                        value={selectedTemplate?.email_subject || ""}
                                        onChange={(e) => setSelectedTemplate((prev) => prev ? { ...prev, email_subject: e.target.value } : null)}
                                        onFocus={() => setLastFocusedField("subject")}
                                        disabled={!selectedTemplate}
                                        className="mt-2 text-foreground"
                                    />
                                </div>

                                <div className="flex items-center justify-between mb-2">
                                    <Label
                                        htmlFor="email-content"
                                        className="text-sm font-medium text-foreground mb-2 flex-shrink-0"
                                    >
                                        Email Content
                                    </Label>
                                    <button
                                        type="button"
                                        onClick={() => setShowVariablesPanel(!showVariablesPanel)}
                                        className="text-xs text-blue-600 hover:text-black flex items-center gap-1"
                                    >
                                        <BracesIcon className="h-3 w-3" />
                                        {showVariablesPanel ? "Hide" : "Show"} Variables
                                    </button>
                                </div>
                                <TextEditor
                                    ref={contentEditorRef}
                                    defaultValue={selectedTemplate?.email_content || ""}
                                    disabled={!selectedTemplate}
                                    onChange={(content) => {
                                        setSelectedTemplate((prev) => prev ? { ...prev, email_content: content } : null);
                                    }}
                                    onFocus={() => setLastFocusedField("content")}
                                />
                                <p
                                    className={`text-xs mt-3 flex-shrink-0 text-muted-foreground`}
                                >
                                    {showVariablesPanel
                                        ? "Click on Variables in the right panel to insert them to your email subject or content."
                                        : "Click the Variables button to see all available personalization options for your email subject or content."}
                                </p>
                            </div>

                            {/* Variables Panel */}
                            {showVariablesPanel && (
                                <div
                                    data-variables-panel
                                    className="w-1/3 flex-shrink-0 min-h-0 flex flex-col"
                                >
                                    <EmailVariablesPanel
                                        onClose={() => setShowVariablesPanel(false)}
                                        onInsertVariable={handleInsertVariable}
                                        isEditMode={true}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Footer - Fixed */}
                        <div className="mt-6 pt-4 border-t border-border flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <Tooltip
                                    side="right"
                                    content={(
                                        <p>Manage your default email settings and templates.</p>
                                    )}
                                >
                                    <Link target="_blank" to="/setting?tab=email-communication&template=assessment-invitation" className="h-auto p-0 text-blue-600 hover:text-blue-700 hover:bg-transparent dark:text-blue-400 dark:hover:text-blue-300 font-normal text-sm hover:underline flex items-center gap-1.5">
                                        <SettingsIcon className="w-3.5 h-3.5" />
                                        Edit Email Settings
                                    </Link>
                                </Tooltip>
                                <div className="flex gap-3">
                                    <Button variant="outline" disabled={isLoading} onClick={() => onClose(false)}>
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleSendInvitation}
                                        disabled={isLoading || isLoadingData || senderName.trim() === "" || !selectedTemplate || listSelectedApplicantEmails.length === 0}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-8 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
                                        style={{ paddingRight: "16px", paddingLeft: "16px" }}
                                    >
                                        {isLoading ? (
                                            <><Loader2Icon className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                                        ) : "Send Invitation"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <RetakeInterviewConfirmDialog
                isOpen={showRetakeWarning}
                onClose={() => setShowRetakeWarning(false)}
                recipientEmails={selectedApplicants.filter(applicant => applicant.is_selected).map(applicant => applicant.main_email)}
                onConfirmRetake={handleSendRetakeConfirmation}
            />
        </>
    );
}
